import React, { useState, useEffect, useRef } from 'react';
import { ComponentLoader } from '../common/Loader';
import { Form } from 'react-bootstrap';
import AddRoleManagement from './AddRole';
import EditRoleManagement from './EditRole';
import axios from 'axios';
import swal from 'sweetalert2';
import WebService from "../../../services/webservice";
import moment from 'moment';
import { FaChevronUp, FaChevronDown, FaTimes } from "react-icons/fa";
import Select from 'react-select';
import './RoleManagement.css';

const statusOptions = [
    { value: 'All', label: 'All Status', color: '#6e7687', bg: '#f4f6f8' },
    { value: 'Active', label: 'Active', color: '#10b981', bg: '#d1fae5' },
    { value: 'Inactive', label: 'Inactive', color: '#ef4444', bg: '#fee2e2' },
];

const customSelectStyles = {
    control: (provided, state) => {
        const selected = state.hasValue ? state.getValue()[0] : null;
        return {
            ...provided,
            width: '150px',
            minHeight: '32px',
            height: '32px',
            borderRadius: '20px',
            backgroundColor: selected ? selected.bg : '#f4f6f8',
            borderColor: selected && selected.value !== 'All' ? selected.color : '#e2e8f0',
            boxShadow: 'none',
            cursor: 'pointer',
            '&:hover': {
                borderColor: selected && selected.value !== 'All' ? selected.color : '#cbd5e1'
            }
        };
    },
    valueContainer: (provided) => ({
        ...provided,
        height: '32px',
        padding: '0 10px'
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    indicatorsContainer: (provided, state) => {
        const selected = state.hasValue ? state.getValue()[0] : null;
        return {
            ...provided,
            height: '32px',
            color: selected ? selected.color : '#6e7687'
        };
    },
    dropdownIndicator: (provided, state) => {
        const selected = state.hasValue ? state.getValue()[0] : null;
        return {
            ...provided,
            color: selected ? selected.color : '#6e7687',
            padding: '4px 8px',
            '&:hover': {
                color: selected ? selected.color : '#333'
            }
        };
    },
    option: (provided, state) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: state.isSelected ? state.data.bg : state.isFocused ? '#f8f9fa' : '#fff',
        color: state.data.color,
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '12px'
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '12px',
        overflow: 'hidden',
        zIndex: 9999,
        marginTop: '4px',
        width: '150px'
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.data.color,
        fontWeight: '600',
        fontSize: '12px',
        margin: '0px'
    })
};

const formatStatusLabel = ({ label, color }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ height: '8px', width: '8px', backgroundColor: color, borderRadius: '50%', display: 'inline-block', marginRight: '8px' }}></span>
        {label}
    </div>
);

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [appMenus, setAppMenus] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        search: '',
        status: 'All',
        startDate: '',
        endDate: ''
    });
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef(null);
    const [sortBy, setSortBy] = useState('createdAt:desc');

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    // Modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState(null);

    useEffect(() => {
        fetchRoles();
    }, [page, pageSize, debouncedSearch, filters.status, filters.startDate, filters.endDate, sortBy]);

    useEffect(() => {
        fetchAppMenus();
    }, []);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const [sortField, sortOrder] = sortBy.split(':');
            const response = await axios.get(`${WebService}api/role-list`, {
                params: { 
                    page, 
                    limit: pageSize,
                    search: debouncedSearch,
                    status: filters.status,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    sortBy: sortField,
                    sortOrder: sortOrder
                }
            });
            if (response.data.success) {
                setRoles(response.data.data);
                setTotalCount(response.data.totalRecords || 0);
                setTotalPages(response.data.totalPages || 0);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            swal.fire('Error', 'Failed to fetch roles.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchAppMenus = async () => {
        try {
            const response = await axios.post(`${WebService}api/app-menus`);
            if (response.data.success) {
                setAppMenus(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching app menus:', error);
        }
    };

    const handleOpenAddModal = () => {
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        setShowAddModal(true);
    };
    const handleOpenEditModal = (role) => {
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        setRoleToEdit(role);
        setShowEditModal(true);
    };

    const handleCloseModals = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setRoleToEdit(null);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, search: val }));

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(1);
        }, 500);
    };

    const clearFilters = () => {
        setFilters({ search: '', status: 'All', startDate: '', endDate: '' });
        setDebouncedSearch('');
        setPage(1);
    };

    const handleSortChange = (field) => {
        const currentField = sortBy.split(':')[0];
        const currentOrder = sortBy.split(':')[1];
        
        if (currentField === field) {
            setSortBy(`${field}:${currentOrder === 'asc' ? 'desc' : 'asc'}`);
        } else {
            setSortBy(`${field}:asc`);
        }
    };

    const renderSortHeader = (label, field, width) => {
        const currentField = sortBy.split(':')[0];
        const currentOrder = sortBy.split(':')[1];
        return (
            <th style={{ width, cursor: 'pointer' }} onClick={() => handleSortChange(field)}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span>{label}</span>
                    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: '11px', gap: '4px' }}>
                        <FaChevronUp color={currentField === field && currentOrder === 'asc' ? '#10b981' : '#cbd5e1'} />
                        <FaChevronDown color={currentField === field && currentOrder === 'desc' ? '#10b981' : '#cbd5e1'} />
                    </span>
                </div>
            </th>
        );
    };

    const handleDelete = (id) => {
            swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        const response = await axios.post(`${WebService}api/delete-role`, { id });
                        if (response.data.success) {
                            swal.fire('Deleted!', 'Role has been deleted.', 'success');
                            fetchRoles();
                        }
                    } catch (error) {
                        console.error('Error deleting role:', error);
                        swal.fire('Error', 'Something went wrong while deleting.', 'error');
                    }
                }
            });
    };

    const handleStatusChange = (id, newStatus) => {
        const actionText = newStatus === 'Inactive' ? 'inactive' : 'active';
        swal.fire({
            title: 'Are you sure?',
            text: `Do you want to mark this role as ${actionText}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: `Yes, mark ${actionText}!`
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    // Send "I" for Inactive if user explicitly requested "I", but typically it's "Inactive" or "Active". We will use the newStatus directly which is 'Inactive' or 'Active'.
                    const statusToSet = newStatus === 'Inactive' ? 'I' : 'Active'; // User specifically requested "I" for inactive
                    const response = await axios.post(`${WebService}api/update-role-status`, { id, status: statusToSet });
                    if (response.data.success) {
                        swal.fire('Updated!', `Role is now ${actionText}.`, 'success');
                        fetchRoles();
                    }
                } catch (error) {
                    console.error('Error updating status:', error);
                    swal.fire('Error', 'Something went wrong while updating status.', 'error');
                }
            }
        });
    };

    const startIndex = (page - 1) * pageSize + 1;
    const endIndex = Math.min(page * pageSize, totalCount);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2>Role Management</h2>
                    <p>Manage roles and permissions</p>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={() => handleOpenAddModal()}
                        className="admin-btn admin-btn-primary"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        + Add Role
                    </button>
                </div>
            </div>
            <div>

                <div >


                    <div style={{ background: '#fff', padding: '10px 15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', border: '1px solid #eaeaea' }}>
                        <Form.Control
                            type="text"
                            placeholder="Search Role Name..."
                            value={filters.search}
                            onChange={handleSearchChange}
                            style={{ width: '200px', height: '32px', fontSize: '13px', borderRadius: '20px', backgroundColor: '#f4f6f8', border: '1px solid #e2e8f0', color: '#333' }}
                        />
                        <Select
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === filters.status) || statusOptions[0]}
                            onChange={(selectedOption) => handleFilterChange('status', selectedOption.value)}
                            styles={customSelectStyles}
                            formatOptionLabel={formatStatusLabel}
                            isSearchable={false}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Form.Control
                                type={filters.startDate ? "date" : "text"}
                                placeholder="Start Date"
                                value={filters.startDate}
                                max={filters.endDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                onFocus={(e) => (e.target.type = "date")}
                                onBlur={(e) => (!e.target.value ? (e.target.type = "text") : null)}
                                style={{ width: '140px', height: '32px', fontSize: '13px', borderRadius: '20px', backgroundColor: '#f4f6f8', border: '1px solid #e2e8f0', color: '#333' }}
                            />
                            <span style={{ color: '#9ca3af', fontSize: '13px' }}>to</span>
                            <Form.Control
                                type={filters.endDate ? "date" : "text"}
                                placeholder="End Date"
                                value={filters.endDate}
                                min={filters.startDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                onFocus={(e) => (e.target.type = "date")}
                                onBlur={(e) => (!e.target.value ? (e.target.type = "text") : null)}
                                style={{ width: '140px', height: '32px', fontSize: '13px', borderRadius: '20px', backgroundColor: '#f4f6f8', border: '1px solid #e2e8f0', color: '#333' }}
                            />
                        </div>
                        {(filters.search || filters.status !== 'All' || filters.startDate || filters.endDate) && (
                            <button
                                onClick={clearFilters}
                                style={{
                                    height: '32px', padding: '0 15px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '20px', 
                                    fontWeight: 600, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer'
                                }}
                            >
                                <FaTimes /> Clear
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div style={{ padding: '80px 0', textAlign: 'center' }}><ComponentLoader color="#4f46e5" size="50px" /></div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="admin-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '10%' }}>S.NO</th>
                                            {renderSortHeader('Role Name', 'roleName', '30%')}
                                            <th style={{ width: '20%' }}>Status</th>
                                            {renderSortHeader('Created Date', 'createdAt', '25%')}
                                            <th style={{ width: '15%' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {roles.length > 0 ? (
                                            roles.map((role, index) => (
                                                <tr key={role._id}>
                                                    <td>{startIndex + index}</td>
                                                    <td>{role.roleName}</td>
                                                    <td>
                                                        <span 
                                                            className={`badge ${role.status === 'Active' ? 'bg-success' : role.status === 'I' || role.status === 'Inactive' ? 'bg-warning' : 'bg-danger'}`}
                                                            style={{ color: (role.status === 'I' || role.status === 'Inactive') ? '#000' : '#fff' }}
                                                        >
                                                            {role.status === 'I' ? 'Inactive' : role.status}
                                                        </span>
                                                    </td>
                                                    <td>{moment(role.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        <div className="action-btns">
                                                            <button 
                                                                className="action-btn btn-edit"
                                                                onClick={() => handleOpenEditModal(role)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button 
                                                                className="action-btn"
                                                                style={{ 
                                                                    backgroundColor: role.status === 'Inactive' || role.status === 'I' ? '#10b981' : '#e79e1fff',
                                                                    color: '#fff'
                                                                }}
                                                                onClick={() => handleStatusChange(role._id, role.status === 'Inactive' || role.status === 'I' ? 'Active' : 'Inactive')}
                                                            >
                                                                {role.status === 'Inactive' || role.status === 'I' ? 'Active' : 'Inactive'}
                                                            </button>
                                                            <button 
                                                                className="action-btn btn-delete"
                                                                onClick={() => handleDelete(role._id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: 'center' }}>No roles found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pagination-music" style={{  padding: '10px 0',  }}>
                                <div className="d-flex align-items-center">
                                    <span className="pagination-info me-2">
                                        Showing {totalCount > 0 ? startIndex : 0} - {endIndex} of {totalCount} Roles. Per page:
                                    </span>
                                    <select 
                                        value={pageSize} 
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setPage(1);
                                        }}
                                        className="form-select"
                                        style={{ 
                                            width: '60px', 
                                            display: 'inline-block', 
                                            marginLeft: '10px',
                                            padding: '2px 15px 2px 8px',
                                            fontSize: '13px',
                                            height: '30px',
                                            minHeight: '30px',
                                            backgroundColor: '#eef2ff',
                                            color: '#4f46e5',
                                            border: '1px solid #c7d2fe',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                                <div className="pagination-buttons">
                                    <button 
                                        className="page-btn" 
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                    >
                                        ◀
                                    </button>
                                    
                                    {[...Array(totalPages)].map((_, i) => (
                                        <button 
                                            key={i + 1}
                                            className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                                            onClick={() => setPage(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}

                                    <button 
                                        className="page-btn" 
                                        onClick={() => setPage(page + 1)}
                                        disabled={page === totalPages || totalPages === 0}
                                    >
                                        ▶
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Add / Edit Modals */}
            <AddRoleManagement
                show={showAddModal}
                onHide={handleCloseModals}
                onSuccess={fetchRoles}
                appMenus={appMenus}
            />
            
            <EditRoleManagement
                show={showEditModal}
                onHide={handleCloseModals}
                onSuccess={fetchRoles}
                appMenus={appMenus}
                roleToEdit={roleToEdit}
            />
        </>
    );
};

export default RoleManagement;
